import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, Droplets } from "lucide-react";
import { MaskLine, Reveal } from "./Reveal";
import { TRUST_METRICS } from "./data";
import { useSiteContent } from "../../context/SiteContent";

export const Hero = ({ onCalc, onWatch }) => {
  const { content } = useSiteContent();
  const heroImg = content?.galleryMedia?.heroBgImageUrl;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="top" ref={ref} className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="inline-flex items-center gap-2 rounded-full border border-helios-line bg-helios-mist px-3.5 py-1.5 mb-7">
                <span className="w-1.5 h-1.5 rounded-full bg-helios-amber animate-pulse" />
                <span className="overline text-slate-500">Solar Robotics · O&amp;M Automation · Est. 2022</span>
              </div>
            </Reveal>

            <h1 className="font-display font-extrabold tracking-tight leading-[1.03] text-helios-slate text-4xl sm:text-5xl lg:text-[3.4rem]">
              <MaskLine delay={0.05}>Autonomous Solar</MaskLine>
              <MaskLine delay={0.15}>Cleaning Robotics &amp;</MaskLine>
              <MaskLine delay={0.25}><span className="text-helios-cyan">Comprehensive O&amp;M.</span></MaskLine>
            </h1>

            <Reveal delay={0.45} className="mt-6 max-w-xl">
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
                Engineered for utility-scale solar farms and commercial rooftops. Eliminate up to{" "}
                <strong className="text-helios-slate font-semibold">99.5% soiling loss</strong> with
                intelligent, waterless robotics.
              </p>
            </Reveal>

            <Reveal delay={0.55} className="mt-8 flex flex-wrap items-center gap-3">
              <button onClick={onCalc}
                className="btn-sheen group inline-flex items-center gap-2 rounded-full bg-helios-slate px-6 py-3.5 text-sm font-semibold text-white hover:bg-helios-slate/90 transition-colors"
                data-testid="hero-calc-roi">
                Calculate Plant ROI
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
              <button onClick={onWatch}
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3.5 text-sm font-semibold text-helios-slate hover:bg-helios-mist transition-colors"
                data-testid="hero-watch-deployments">
                <Play size={15} className="fill-helios-slate" /> Watch Field Deployments
              </button>
            </Reveal>
          </div>

          <div className="lg:col-span-6">
            <Reveal delay={0.3}>
              <div className="relative rounded-[1.75rem] overflow-hidden border border-helios-line shadow-[0_30px_60px_-30px_rgba(15,23,42,0.35)] aspect-[4/3.4]">
                {heroImg && (
                  <motion.img src={heroImg} alt="Utility-scale solar farm"
                    style={{ y: yImg, scale: scaleImg }} className="absolute inset-0 w-full h-full object-cover" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-helios-slate/40 via-transparent to-transparent" />
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.6 }}
                  className="absolute bottom-4 left-4 right-4 flex items-center gap-3 rounded-2xl bg-white/85 backdrop-blur-md border border-white/60 p-3.5">
                  <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-helios-cyan/10 text-helios-cyan shrink-0">
                    <Droplets size={20} />
                  </div>
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Waterless Dry Cleaning</p>
                    <p className="font-display font-bold text-helios-slate leading-tight">Zero water. Zero soiling loss.</p>
                  </div>
                </motion.div>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.2} className="mt-14 md:mt-20">
          <div className="grid grid-cols-2 lg:grid-cols-4 rounded-2xl border border-helios-line bg-white overflow-hidden">
            {TRUST_METRICS.map((m, i) => (
              <div key={m.label}
                className={`p-6 lg:p-7 ${i !== 0 ? "border-t lg:border-t-0 lg:border-l" : ""} ${i === 2 ? "lg:border-l border-t" : ""} border-helios-line`}
                data-testid={`trust-metric-${i}`}>
                <p className="font-mono text-3xl lg:text-4xl font-bold text-helios-slate tracking-tight">{m.value}</p>
                <p className="mt-2 font-display font-semibold text-sm text-helios-slate">{m.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{m.sub}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
